import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { PALETTE } from '@/core/lib/colors';
import { priorityToInt } from '@/shared/lib/priority';
import type { Priority } from '@/shared/lib/priority';
import { cn } from '@/core/lib/utils';
import { useAuthStore } from '@/core/stores/auth.store';
import { useOrganizations } from '@/shared/hooks/useOrganizations';
import { projectsService, useProjectMembers } from '@/features/projects/public';
import { useMembers } from '@/features/members/public';
import { useProjectStatuses } from '@/features/tasks/public';
import { useBoardGroups, useBoards, useBoardStatuses } from '@/features/boards/public';
import { useMyProjectsSearch } from './useMyProjectsSearch';
import { matchOptionByName } from '../utils/matchOptionByName';
import type { NameMatch } from '../utils/matchOptionByName';
import type { NoteTaskProposal } from '../types';
import { Flag } from 'lucide-react';

/**
 * Priorities in descending order, with the flag colour each gets — same colours
 * as the task list picker. Codes come from `priorityToInt`, never inlined: the
 * AI proposal and the convert endpoint both speak the domain enum.
 */
const PRIORITIES: { key: Priority; color: string }[] = [
  { key: 'urgent', color: 'text-chart-5' },
  { key: 'high', color: 'text-chart-3' },
  { key: 'medium', color: 'text-chart-4' },
  { key: 'low', color: 'text-chart-2' },
];

function deterministicColor(userId: string): string {
  const hash = userId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

/** Id of the single option in a list, or null when there is a real choice to make. */
function onlyOption(options: { value: string }[]): string | null {
  return options.length === 1 ? options[0].value : null;
}

/** Only a single unambiguous match is ever selected; the rest is explained, not guessed. */
function selected(match: NameMatch): string | null {
  return match.kind === 'matched' ? match.value : null;
}

/**
 * Every option list the convert-to-task form offers, plus the name the note
 * mentioned resolved against each one. Separated from the form itself because
 * none of it depends on `react-hook-form` — only on the project/board the
 * form currently has selected and the proposal the interpretation returned.
 */
export function useConvertToTaskOptions(
  projectId: string,
  boardId: string,
  proposal: NoteTaskProposal | null,
) {
  const { t } = useTranslation('notes');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: organizations = [] } = useOrganizations();

  // Two reads, both server-side searches so neither is capped by a page size.
  // The first page is only what the model gets as context and what tells us whether
  // there is a single project to preselect; the picker fetches its own pages.
  const {
    data: firstProjects,
    totalCount: projectCount,
    isLoading: projectsLoading,
  } = useMyProjectsSearch('');

  // Resolves the name the note gave against every project, not just a page of them.
  const { data: proposedProjectMatches } = useMyProjectsSearch(proposal?.projectName ?? '', 0, 10, {
    enabled: !!proposal?.projectName,
  });

  const { data: projectMembersData } = useProjectMembers(projectId || '');
  const projectMembers = projectMembersData ?? [];

  // Looked up by name so the payload stays small: this only answers "does this
  // person exist in the organization", to tell apart a name that is not a project
  // member from a name that is nobody here.
  const { data: orgMembers } = useMembers(proposal?.assigneeName ?? '', 0, 5);

  const organizationOptions = organizations.map((org) => ({ value: org.id, label: org.name }));

  const toOption = (p: { id: string | null; name: string | null }) => ({
    value: p.id as string,
    label: p.name ?? '',
  });

  const contextProjects = firstProjects.filter((p) => p.id !== null).map(toOption);
  const projectCandidates = proposedProjectMatches.filter((p) => p.id !== null).map(toOption);

  /** Fetches a page for the project picker — search runs on the server, so nothing is out of reach. */
  async function fetchProjects({ search, pageSize }: { search: string; pageSize: number }) {
    const result = await projectsService.getMyProjects({
      SearchTerm: search || undefined,
      Page: 1,
      PageSize: pageSize,
      IncludeArchived: false,
    });
    return {
      items: (result.items ?? [])
        .filter((p) => p.id !== null)
        .map((p) => ({ id: p.id as string, label: p.name ?? '' })),
      total: result.totalCount,
    };
  }

  const memberOptions = projectMembers.map((m) => ({
    value: m.userId,
    label: m.displayName,
    icon: (
      <UserAvatar
        name={m.displayName}
        color={deterministicColor(m.userId)}
        src={m.avatarUrl ?? undefined}
        size="xs"
      />
    ),
  }));

  const priorityOptions = PRIORITIES.map((p) => ({
    value: String(priorityToInt(p.key)),
    label: t(`convertToTask.priorities.${p.key}`),
    icon: <Flag className={cn('size-3.5', p.color)} aria-hidden />,
  }));

  const { boards } = useBoards(projectId || '');
  const boardOptions = boards
    .filter((b) => b.id !== null)
    .map((b) => ({ value: b.id as string, label: b.name ?? '' }));

  const { statuses } = useBoardStatuses(boardId || '');
  const statusOptions = statuses
    .filter((s) => s.id !== null)
    .map((s) => ({ value: s.id as string, label: s.name ?? '' }));

  // The picker takes the status object, so the same list arrives already adapted.
  const { statuses: projectStatuses } = useProjectStatuses(boardId || '');

  const { groups } = useBoardGroups(boardId || '');
  const groupOptions = groups
    .filter((g) => g.id !== null)
    .map((g) => ({ value: g.id as string, label: g.name ?? '' }));

  // One option is not a choice. Preselecting it is what keeps the dependent
  // fields — assignee, status, group, tags — from sitting disabled with nothing
  // for the user to do about it.
  const onlyBoard = onlyOption(boardOptions);
  const onlyStatus = onlyOption(statusOptions);
  // A single project is not a choice — and `totalCount` says so for the whole set,
  // not just the page in hand.
  const onlyProject = projectCount === 1 ? (contextProjects[0]?.value ?? null) : null;

  // Every name the note mentioned, resolved against what exists. Statuses and groups
  // only exist once a board is chosen, so they resolve here rather than at
  // interpretation time — and re-resolve as those lists arrive.
  const projectMatch = matchOptionByName(projectCandidates, proposal?.projectName);
  const statusMatch = matchOptionByName(statusOptions, proposal?.statusName);
  const groupMatch = matchOptionByName(groupOptions, proposal?.groupName);
  const assigneeMatch = matchOptionByName(memberOptions, proposal?.assigneeName);

  const proposedProject = selected(projectMatch);
  const proposedStatus = selected(statusMatch);
  const proposedGroup = selected(groupMatch);

  /** Keeps the trigger showing a name for a project the loaded page does not contain. */
  const selectedProjectOption = [...projectCandidates, ...contextProjects].find(
    (option) => option.value === projectId,
  );

  // "para mí" resolves to the signed-in user; any other name goes through the same
  // matcher as the rest, so an abbreviated name resolves here too. Members only exist
  // once a project is chosen, so this settles when that list arrives.
  const proposedAssignee = proposal?.assigneeIsCaller
    ? (memberOptions.find((m) => m.value === currentUserId)?.value ?? null)
    : selected(assigneeMatch);

  return {
    projectsLoading,
    organizationOptions,
    contextProjects,
    projectCandidates,
    fetchProjects,
    memberOptions,
    priorityOptions,
    boardOptions,
    statusOptions,
    projectStatuses,
    groupOptions,
    onlyBoard,
    onlyStatus,
    onlyProject,
    projectMatch,
    statusMatch,
    groupMatch,
    assigneeMatch,
    proposedProject,
    proposedStatus,
    proposedGroup,
    proposedAssignee,
    selectedProjectOption,
    orgMembers,
  };
}
